import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_URL from "../apiConfig";

const emptyForm = {
  card_type: "petition",
  category: "Personal",
  title: "",
  scripture_reference: "",
  scripture_text: "",
  content: "",
  prayer_text: "",
  answered_note: "",
};

const categories = [
  "Familia",
  "Matrimonio",
  "Salud",
  "Finanzas",
  "País",
  "Propósito",
  "Personal",
  "Otro",
];

function getSessionToken() {
  const session = JSON.parse(localStorage.getItem("zedro_session"));
  return session?.token;
}

function formatDate(value) {
  if (!value) return "";

  return new Date(value).toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getCardTypeLabel(type) {
  if (type === "battle_plan") return "Plan de Batalla";
  if (type === "prayer") return "Oración";
  return "Petición";
}

export default function Conexion() {
  const navigate = useNavigate();

  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingWallSnapshot, setSavingWallSnapshot] = useState(false);

  const [form, setForm] = useState(emptyForm);
  const [editingCardId, setEditingCardId] = useState(null);
  const [message, setMessage] = useState("");
  const [orderingPrayer, setOrderingPrayer] = useState(false);
  const [activeComposer, setActiveComposer] = useState(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [scriptureCategories, setScriptureCategories] = useState([]);
  const [selectedScriptureCategory, setSelectedScriptureCategory] = useState("");
  const [selectedScriptureId, setSelectedScriptureId] = useState("");

  useEffect(() => {
    loadConnectionCards();
  }, []);

  useEffect(() => {
    async function loadScriptureLibrary() {
      try {
        const res = await fetch(`${API_URL}/api/connection/scriptures`, {
          headers: {
            Authorization: `Bearer ${getSessionToken()}`,
          },
        });

        const json = await res.json();

        if (!res.ok || !json.ok) {
          throw new Error(json.error || "No se pudo cargar la biblioteca bíblica.");
        }

        setScriptureCategories(json.categories || []);
      } catch (err) {
        console.error("[SCRIPTURE_LIBRARY_ERROR]", err);
        setScriptureCategories([]);
      }
    }

    loadScriptureLibrary();
  }, []);

  async function loadConnectionCards() {
    try {
      const res = await fetch(`${API_URL}/api/connection`, {
        headers: {
          Authorization: `Bearer ${getSessionToken()}`,
        },
      });

      const json = await res.json();

      if (json.ok) {
        setCards(json.cards || []);
      }
    } catch (err) {
      console.error("[CONNECTION_LOAD_ERROR]", err);
    } finally {
      setLoading(false);
    }
  }

  const battlePlanCards = useMemo(
    () => cards.filter((card) => card.card_type === "battle_plan" && card.status === "active"),
    [cards]
  );

  const activePetitions = useMemo(
    () =>
      cards.filter(
        (card) => card.card_type === "petition" && card.status === "active"
      ),
    [cards]
  );

  const prayerCards = useMemo(
    () =>
      cards.filter(
        (card) => card.card_type === "prayer" && card.status === "active"
      ),
    [cards]
  );

  const answeredCards = useMemo(
    () => cards.filter((card) => card.status === "answered"),
    [cards]
  );

  function updateForm(field, value) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  }

  function resetForm() {
    setForm(emptyForm);
    setEditingCardId(null);
    setMessage("");
    setActiveComposer(null);
    setSelectedScriptureCategory("");
    setSelectedScriptureId("");
  }

  function startEdit(card) {
    setEditingCardId(card.id);
    setActiveComposer(card.card_type || "petition");

    setForm({
      card_type: card.card_type || "petition",
      category: card.category || "Personal",
      title: card.title || "",
      scripture_reference: card.scripture_reference || "",
      scripture_text: card.scripture_text || "",
      content: card.content || "",
      prayer_text: card.prayer_text || "",
      answered_note: card.answered_note || "",
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }

  function openComposer(type) {
    setEditingCardId(null);
    setMessage("");

    setForm({
      ...emptyForm,
      card_type: type,
      category: type === "battle_plan" ? "Propósito" : "Personal",
    });

    setSelectedScriptureCategory("");
    setSelectedScriptureId("");
    
    setActiveComposer(type);
  }

  function closeComposer() {
    resetForm();
  }

  function getSelectedCategoryVerses() {
    const selectedCategory = scriptureCategories.find(
      (item) => item.category === selectedScriptureCategory
    );

    return selectedCategory?.verses || [];
  }

  function handleScriptureCategoryChange(category) {
    setSelectedScriptureCategory(category);
    setSelectedScriptureId("");

    updateForm("category", category || "Personal");
    updateForm("title", "");
    updateForm("scripture_reference", "");
  }

  function handleScriptureThemeChange(scriptureId) {
    setSelectedScriptureId(scriptureId);

    if (scriptureId === "custom") {
      updateForm("title", "Otro");
      updateForm("scripture_reference", "");
      return;
    }

    const selectedVerse = getSelectedCategoryVerses().find(
      (verse) => verse.id === scriptureId
    );

    if (!selectedVerse) {
      updateForm("title", "");
      updateForm("scripture_reference", "");
      return;
    }

     updateForm("title", selectedVerse.theme || "");
     updateForm("scripture_reference", selectedVerse.reference || "");
  }

  function getComposerTitle() {
  if (editingCardId) return "Editar tarjeta";

  if (form.card_type === "battle_plan") return "Agregar Palabra al muro";
  if (form.card_type === "petition") return "Presentar petición";
  if (form.card_type === "prayer") return "Escribir oración";

  return "Agregar al muro";
}

function getComposerSubmitLabel() {
  if (saving) return "Guardando...";
  if (editingCardId) return "Guardar cambios";

  if (form.card_type === "battle_plan") return "Agregar al Plan de Batalla";
  if (form.card_type === "petition") return "Agregar petición";
  if (form.card_type === "prayer") return "Guardar oración";

  return "Agregar al muro";
}

  async function orderPrayerWithAI() {
  const hasContent =
    form.scripture_reference.trim() ||
    form.scripture_text.trim() ||
    form.content.trim() ||
    form.prayer_text.trim();

  if (!hasContent) {
    setMessage("Escribe primero la Palabra, la petición o una idea de tu oración.");
    return;
  }

  try {
    setOrderingPrayer(true);
    setMessage("");

    const res = await fetch(`${API_URL}/api/connection/prayer-assist`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getSessionToken()}`,
      },
      body: JSON.stringify({
        scripture_reference: form.scripture_reference,
        scripture_text: form.scripture_text,
        content: form.content,
        prayer_text: form.prayer_text,
      }),
    });

    const json = await res.json();

    if (!res.ok || !json.ok) {
      throw new Error(json.message || json.error || "No se pudo ordenar la oración.");
    }

    updateForm("prayer_text", json.prayer || "");
    setMessage("Oración ordenada. Puedes editarla antes de agregarla al muro.");
  } catch (err) {
    console.error("[ORDER_PRAYER_ERROR]", err);
    setMessage("No se pudo ordenar la oración. Intenta nuevamente.");
  } finally {
    setOrderingPrayer(false);
  }
}

  async function saveWallSnapshotToJournal() {
    try {
      setSavingWallSnapshot(true);
      setMessage("");

      const res = await fetch(`${API_URL}/api/connection/save-journal-snapshot`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getSessionToken()}`,
        },
      });

      const json = await res.json();

      if (!res.ok || !json.ok) {
        throw new Error(
          json.message || json.error || "No se pudo guardar el muro en Bitácora."
        );
      }

      setMessage("Muro guardado en Bitácora.");
    } catch (err) {
      console.error("[SAVE_WALL_SNAPSHOT_ERROR]", err);
      setMessage(err.message || "No se pudo guardar el muro en Bitácora.");
    } finally {
      setSavingWallSnapshot(false);
    }
  }

  async function saveCard() {
    if (!form.title.trim()) {
      setMessage("Escribe un título para agregarlo al muro.");
      return;
    }

    try {
      setSaving(true);
      setMessage("");

      const url = editingCardId
        ? `${API_URL}/api/connection/${editingCardId}`
        : `${API_URL}/api/connection`;

      const method = editingCardId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getSessionToken()}`,
        },
        body: JSON.stringify(form),
      });

      const json = await res.json();

      if (!res.ok || !json.ok) {
        throw new Error(json.error || "No se pudo guardar la tarjeta.");
      }

      setCards((current) => {
        if (editingCardId) {
          return current.map((card) =>
            card.id === editingCardId ? json.card : card
          );
        }

        return [json.card, ...current];
      });

      setMessage(editingCardId ? "Tarjeta actualizada." : "Tarjeta agregada al muro.");
      resetForm();
    } catch (err) {
      console.error("[CONNECTION_SAVE_ERROR]", err);
      setMessage("No se pudo guardar. Revisa e intenta nuevamente.");
    } finally {
      setSaving(false);
    }
  }

  async function deleteCard(cardId) {
    const confirmDelete = window.confirm("¿Deseas eliminar esta tarjeta del muro?");
    if (!confirmDelete) return;

    try {
      const res = await fetch(`${API_URL}/api/connection/${cardId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getSessionToken()}`,
        },
      });

      const json = await res.json();

      if (!res.ok || !json.ok) {
        throw new Error(json.error || "No se pudo eliminar.");
      }

      setCards((current) => current.filter((card) => card.id !== cardId));
    } catch (err) {
      console.error("[CONNECTION_DELETE_ERROR]", err);
      alert("No se pudo eliminar la tarjeta.");
    }
  }

  async function markAsAnswered(card) {
    const answeredNote = window.prompt(
      "¿Cómo fue respondida esta petición o qué victoria quieres registrar?"
    );

    if (answeredNote === null) return;

    try {
      const res = await fetch(`${API_URL}/api/connection/${card.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getSessionToken()}`,
        },
        body: JSON.stringify({
          ...card,
          status: "answered",
          answered_note: answeredNote,
        }),
      });

      const json = await res.json();

      if (!res.ok || !json.ok) {
        throw new Error(json.error || "No se pudo mover a victorias.");
      }

      setCards((current) =>
        current.map((item) => (item.id === card.id ? json.card : item))
      );
    } catch (err) {
      console.error("[CONNECTION_ANSWERED_ERROR]", err);
      alert("No se pudo mover a Victorias.");
    }
  }

  function renderCard(card, variant = "default") {
    return (
      <article
        key={card.id}
        className={`conexionCard ${
          card.card_type === "battle_plan" ? "is-battle" : ""
        } ${variant === "victory" ? "is-victory" : ""}`}
      >
        <div className="conexionCardTop">
          {card.category && <span>{card.category}</span>}
          {card.created_at && <em>{formatDate(card.created_at)}</em>}
        </div>

        <h3>{card.title}</h3>

        {card.scripture_reference && (
          <p className="conexionScriptureRef">{card.scripture_reference}</p>
        )}

        {card.scripture_text && (
          <p className="conexionScriptureText">{card.scripture_text}</p>
        )}

        {card.content && <p className="conexionCardText">{card.content}</p>}

        {card.prayer_text && (
          <div className="conexionPrayerBox">
            <strong>Oración</strong>
            <p>{card.prayer_text}</p>
          </div>
        )}

        {card.status === "answered" && card.answered_note && (
          <div className="conexionVictoryNote">
            <strong>Victoria registrada</strong>
            <p>{card.answered_note}</p>
            <small>{formatDate(card.answered_at)}</small>
          </div>
        )}

        <div className="conexionCardActions">
          <button type="button" onClick={() => startEdit(card)}>
            Editar
          </button>

          {card.status === "active" && (
            <button type="button" onClick={() => markAsAnswered(card)}>
              Mover a Victorias
            </button>
          )}

          <button type="button" onClick={() => deleteCard(card.id)}>
            Eliminar
          </button>
        </div>
      </article>
    );
  }

  return (
    <main className="conexionPage">
      <section className="conexionShell">
        <button
          type="button"
          className="z-back-pill conexionBack"
          onClick={() => navigate("/dashboard")}
        >
          ← Volver al panel central
        </button>

        <header className="conexionHeader">
          <div>
            <span>Cuarto de Guerra</span>
            <h1>Conexión ZEDRO</h1>

            <p className="conexionHeroText">
              Este es tu espacio de conexión con Dios: un muro de oración que no depende
              de un lugar físico, sino de un corazón dispuesto a volver a la Palabra,
              presentar sus cargas y permanecer firme en oración.
            </p>

            <div className="conexionGuideAction">
              <button type="button" onClick={() => setGuideOpen(true)}>
                Ver guía rápida
              </button>
            </div>
          </div>
        </header>

        <div className="conexionSanctuary">
          <section className="conexionWall">
            <div className="conexionWallIntro">
              <span>Muro de Oración</span>

              <button
                type="button"
                className="conexionSaveWallButton"
                onClick={saveWallSnapshotToJournal}
                disabled={savingWallSnapshot}
              >
                {savingWallSnapshot ? "Guardando..." : "Guardar en Bitácora"}
              </button>
            </div>

            {loading ? (
              <p className="conexionEmpty">Cargando Conexión ZEDRO...</p>
             ) : (
               <div className="conexionBoard conexionBoard--sanctuary">
                <section className="conexionZone conexionZone--battle">
                  <div className="conexionZoneHead">
                    <span className="conexionZoneIcon">✦</span>
                    <div>
                      <h3>Plan de Batalla</h3>
                      <p>Verdades bíblicas que sostienen tu oración.</p>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="conexionZoneAdd"
                    onClick={() => openComposer("battle_plan")}
                  >
                    + Agregar Palabra al muro
                  </button>

                  <div className="conexionCards">
                    {battlePlanCards.length > 0 ? (
                      battlePlanCards.map((card) => renderCard(card))
                    ) : (
                      <p className="conexionEmpty">
                        Escribe versículos, promesas o verdades bíblicas que estés
                        orando.
                      </p>
                    )}
                  </div>
                </section>

                <section className="conexionZone conexionZone--petition">
                  <div className="conexionZoneHead">
                    <span className="conexionZoneIcon">◌</span>
                    <div>
                      <h3>Peticiones activas</h3>
                       <p>Lo que hoy estás presentando delante de Dios.</p>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="conexionZoneAdd"
                    onClick={() => openComposer("petition")}
                  >
                    + Presentar petición
                  </button>

                  <div className="conexionCards">
                    {activePetitions.length > 0 ? (
                      activePetitions.map((card) => renderCard(card))
                    ) : (
                        <p className="conexionEmpty">
                       Aquí aparecerán tus peticiones activas.
                     </p>
                    )}
                   </div>
                 </section>

                 <section className="conexionZone conexionZone--prayer">
                  <div className="conexionZoneHead">
                    <span className="conexionZoneIcon">☾</span>
                    <div>
                      <h3>Oraciones</h3>
                      <p>Palabras escritas desde tu proceso y tu búsqueda.</p>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="conexionZoneAdd"
                    onClick={() => openComposer("prayer")}
                  >
                    + Escribir oración
                  </button>

                  <div className="conexionCards">
                     {prayerCards.length > 0 ? (
                      prayerCards.map((card) => renderCard(card))
                    ) : (
                      <p className="conexionEmpty">
                        Aquí aparecerán tus oraciones guardadas.
                      </p>
                    )}
                  </div>
                </section>

                <section className="conexionZone conexionZone--victory">
                  <div className="conexionZoneHead">
                    <span className="conexionZoneIcon">✓</span>
                    <div>
                      <h3>Victorias</h3>
                      <p>Respuestas que fortalecen tu fe y tu memoria.</p>
                    </div>
                  </div>

                  <div className="conexionCards">
                    {answeredCards.length > 0 ? (
                      answeredCards.map((card) => renderCard(card, "victory"))
                    ) : (
                      <p className="conexionEmpty">
                        Cuando una petición sea respondida, muévela aquí para recordar
                        la fidelidad de Dios.
                      </p>
                    )}
                  </div>
                </section>
              </div>
            )}
          </section>
        </div>

        {activeComposer && (
          <div className="conexionComposerOverlay">
            <div className="conexionComposerPanel">
              <div className="conexionComposerHead">
                <div>
                  <span>Control de mando</span>
                  <h2>{getComposerTitle()}</h2>
                </div>

                <button type="button" onClick={closeComposer}>
                  ×
                </button>
              </div>

              <div className="conexionComposerGrid">
                {form.card_type === "petition" && (
                  <>
                    <label>
                      Categoría
                      <select
                        value={form.category}
                        onChange={(e) => updateForm("category", e.target.value)}
                      >
                        {categories.map((category) => (
                          <option key={category} value={category}>
                            {category}
                           </option>
                        ))}
                      </select>
                    </label>

                    <label>
                      Título
                      <input
                        value={form.title}
                        onChange={(e) => updateForm("title", e.target.value)}
                        placeholder="Ej: Orar por mi familia"
                      />
                    </label>
                  </>
                )}

                {form.card_type === "prayer" && (
                  <label className="conexionComposerFull">
                    Título
                    <input
                      value={form.title}
                      onChange={(e) => updateForm("title", e.target.value)}
                      placeholder="Ej: Oración por dirección"
                    />
                  </label>
                )}

                {form.card_type === "battle_plan" && (
                  <>
                    <label>
                      Categoría bíblica
                      <select
                        value={selectedScriptureCategory}
                        onChange={(e) => handleScriptureCategoryChange(e.target.value)}
                      >
                        <option value="">Selecciona una categoría</option>
                        {scriptureCategories.map((item) => (
                          <option key={item.category} value={item.category}>
                            {item.category}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label>
                      Tema / enfoque
                      <select
                        value={selectedScriptureId}
                        onChange={(e) => handleScriptureThemeChange(e.target.value)}
                        disabled={!selectedScriptureCategory}
                      >
                        <option value="">
                          {selectedScriptureCategory
                            ? "Selecciona un tema"
                            : "Primero elige una categoría"}
                        </option>

                        {getSelectedCategoryVerses().map((verse) => (
                          <option key={verse.id} value={verse.id}>
                            {verse.theme}
                          </option>
                        ))}

                        {selectedScriptureCategory && (
                          <option value="custom">Otro / escribir mi propia referencia</option>
                        )}
                      </select>
                    </label>

                    <label>
                      Referencia bíblica
                      <input
                        value={form.scripture_reference}
                        onChange={(e) => updateForm("scripture_reference", e.target.value)}
                        placeholder="Ej: Romanos 12:15"
                        readOnly={selectedScriptureId && selectedScriptureId !== "custom"}
                      />
                    </label>

                    <label>
                      Título
                      <input
                        value={form.title}
                        onChange={(e) => updateForm("title", e.target.value)}
                        placeholder="Ej: Fortaleza en la debilidad"
                      />
                    </label>

                    <label className="conexionComposerFull">
                      Verdad que vas a orar
                      <p className="conexionScriptureHelp">
                        Busca este pasaje en tu Biblia, léelo con calma y escribe aquí la verdad
                        que vas a llevar a tu oración.
                      </p>

                       <textarea
                        value={form.scripture_text}
                        onChange={(e) => updateForm("scripture_text", e.target.value)}
                        placeholder="Escribe aquí la verdad bíblica que vas a orar..."
                      />
                    </label>
                  </>
                )}

                {form.card_type === "petition" && (
                  <>
                    <label className="conexionComposerFull">
                      Petición
                      <textarea
                        value={form.content}
                        onChange={(e) => updateForm("content", e.target.value)}
                        placeholder="Escribe lo que estás presentando delante de Dios..."
                      />
                    </label>
                  </>
                )}

                {form.card_type === "prayer" && (
                  <>
                    <label className="conexionComposerFull">
                      Lo que quieres ordenar en oración
                      <textarea
                        value={form.content}
                        onChange={(e) => updateForm("content", e.target.value)}
                        placeholder="Escribe lo que estás sintiendo, pensando o presentando delante de Dios..."
                      />
                    </label>

                    <label className="conexionComposerFull">
                      <div className="conexionPrayerAssistHeader">
                        <span>Oración</span>

                        <button
                          type="button"
                          className="conexionPrayerAssistButton"
                          onClick={orderPrayerWithAI}
                          disabled={orderingPrayer}
                        >
                          {orderingPrayer ? "Ordenando..." : "Ordenar mi oración"}
                        </button>
                      </div>

                      <p className="conexionPrayerAssistNote">
                        La IA no ora por ti. Solo te ayuda a ordenar tus palabras para expresar tu oración con más claridad.
                      </p>

                      <textarea
                        value={form.prayer_text}
                        onChange={(e) => updateForm("prayer_text", e.target.value)}
                        placeholder="Escribe tu oración o usa el botón para ordenar lo que ya escribiste..."
                      />
                    </label>
                  </>
                )}
              </div>

              <div className="conexionCommandActions">
                <button type="button" onClick={saveCard} disabled={saving}>
                  {getComposerSubmitLabel()}
                </button>

                {editingCardId && (
                  <button type="button" onClick={resetForm}>
                    Cancelar edición
                  </button>
                )}
              </div>

              {message && <p className="conexionMessage">{message}</p>}
             </div>
           </div>
        )}

        {guideOpen && (
          <div className="conexionGuideOverlay">
            <div className="conexionGuideModal">
              <div className="conexionGuideModalHead">
                <div>
                  <span>Guía breve</span>
                  <h2>Cómo usar tu Muro de Oración</h2>
                </div>

                <button type="button" onClick={() => setGuideOpen(false)}>
                  ×
                </button>
              </div>

              <video
                controls
                preload="metadata"
                className="conexionGuideModalVideo"
              >
                <source src="PEGA_AQUI_LA_URL_DEL_VIDEO" type="video/mp4" />
                Tu navegador no puede reproducir este video.
              </video>
            </div>
          </div>
        )}
      
      </section>

      <style>
        {`
          .conexionPage {
            min-height: 100vh;
            padding: 2rem 1.25rem 4rem;
            background:
              radial-gradient(circle at top left, rgba(204, 163, 82, 0.12), transparent 34%),
              var(--page-bg);
            color: #0f2f46;
          }

          .conexionShell {
            max-width: 1320px;
            margin: 0 auto;
          }

          .conexionBack {
            margin-bottom: 1.4rem;
          }

          .conexionHeader {
            display: flex;
            justify-content: space-between;
            gap: 1rem;
            margin-bottom: 1.6rem;
          }

          .conexionHeader span,
          .conexionCommandHead span,
          .conexionWallIntro span {
            display: inline-flex;
            margin-bottom: 0.65rem;
            padding: 0.38rem 0.75rem;
            border-radius: 999px;
            background: rgba(204, 163, 82, 0.12);
            color: #9b7527;
            font-size: 0.72rem;
            font-weight: 900;
            letter-spacing: 0.12em;
            text-transform: uppercase;
          }

          .conexionHeader h1 {
            margin: 0;
            font-size: clamp(1.9rem, 3.4vw, 2.85rem);
            font-weight: 400;
            letter-spacing: -0.045em;
            line-height: 1.05;
          }

          .conexionHeader p {
            max-width: 820px;
            margin: 0.9rem 0 0;
            color: rgba(15, 47, 70, 0.68);
            font-size: 1rem;
            line-height: 1.65;
          }

          .conexionHeader > div {
            width: 100%;
          }

          .conexionHeroText {
            max-width: 900px;
            margin: 1.1rem 0 0;
            color: rgba(15, 47, 70, 0.76);
            font-size: 1.04rem;
            line-height: 1.72;
          }
                
          .conexionLayout {
            display: grid;
            grid-template-columns: 360px minmax(0, 1fr);
            gap: 1.2rem;
            align-items: start;
          }

          .conexionCommand {
            position: sticky;
            top: 96px;
            padding: 1.15rem;
            border-radius: 28px;
            background: rgba(255, 255, 255, 0.92);
            border: 1px solid rgba(15, 47, 70, 0.08);
            box-shadow: 0 18px 45px rgba(15, 47, 70, 0.06);
          }

          .conexionCommandHead h2,
          .conexionWallIntro h2 {
            margin: 0;
            font-size: 1.35rem;
            font-weight: 500;
            letter-spacing: -0.025em;
          }

          .conexionCommand label {
            display: grid;
            gap: 0.38rem;
            margin-top: 0.85rem;
            color: rgba(15, 47, 70, 0.68);
            font-size: 0.78rem;
            font-weight: 900;
          }

          .conexionCommand input,
          .conexionCommand select,
          .conexionCommand textarea {
            width: 100%;
            box-sizing: border-box;
            border-radius: 16px;
            border: 1px solid rgba(15, 47, 70, 0.12);
            background: #fff;
            color: #0f2f46;
            font-family: Questrial, sans-serif;
            font-size: 0.96rem;
            line-height: 1.55;
            outline: none;
          }

          .conexionCommand input,
          .conexionCommand select {
            min-height: 44px;
            padding: 0 0.85rem;
          }

          .conexionCommand textarea {
            min-height: 92px;
            resize: vertical;
            padding: 0.8rem 0.85rem;
          }

          .conexionCommand input:focus,
          .conexionCommand select:focus,
          .conexionCommand textarea:focus {
            border-color: rgba(204, 163, 82, 0.44);
            box-shadow: 0 0 0 4px rgba(204, 163, 82, 0.08);
          }

          .conexionCommandActions {
            display: flex;
            flex-wrap: wrap;
            gap: 0.6rem;
            margin-top: 1rem;
          }

          .conexionCommandActions button {
            min-height: 42px;
            padding: 0 1rem;
            border-radius: 999px;
            border: 1px solid rgba(204, 163, 82, 0.34);
            background: #fff;
            color: #7b6430;
            font-family: Questrial, sans-serif;
            font-weight: 900;
            cursor: pointer;
          }

          .conexionCommandActions button:first-child {
            background: linear-gradient(180deg, rgba(204, 163, 82, 0.12), #fff);
          }

          .conexionCommandActions button:disabled {
            opacity: 0.62;
            cursor: not-allowed;
          }

          .conexionMessage {
            margin: 0.85rem 0 0;
            color: rgba(15, 47, 70, 0.62);
            font-size: 0.9rem;
          }

          .conexionWall {
            margin-top: 2rem;
            padding: 1.3rem 1.3rem 1.5rem;
            border-radius: 32px;
            border: 1px solid rgba(204, 163, 82, 0.34);
            background:
              radial-gradient(circle at 24% 10%, rgba(227, 197, 112, 0.24), transparent 18%),
              radial-gradient(circle at 52% 0%, rgba(227, 197, 112, 0.12), transparent 20%),
              radial-gradient(circle at 78% 12%, rgba(58, 89, 120, 0.18), transparent 18%),
              linear-gradient(135deg, #0c263a 0%, #13354d 38%, #0f2f46 68%, #081f31 100%);
            box-shadow:
              inset 0 1px 0 rgba(255, 255, 255, 0.08),
              0 24px 60px rgba(6, 18, 28, 0.24);
          }

          .conexionWallIntro {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            margin-bottom: 1.15rem;
          }

          .conexionWallIntro span {
            display: inline-flex;
            align-items: center;
            padding: 0.45rem 0.9rem;
            border-radius: 999px;
            background: rgba(255, 248, 232, 0.14);
            border: 1px solid rgba(204, 163, 82, 0.28);
            color: #e3c570;
            font-size: 0.78rem;
            font-weight: 800;
            letter-spacing: 0.11em;
            text-transform: uppercase;
          }

          .conexionBoard {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 0.9rem;
          }

          .conexionColumn {
            min-height: 520px;
            padding: 0.85rem;
            border-radius: 26px;
            background: rgba(255, 255, 255, 0.72);
            border: 1px solid rgba(15, 47, 70, 0.07);
          }

          .conexionColumnHead {
            display: flex;
            align-items: center;
            gap: 0.7rem;
            margin-bottom: 0.8rem;
          }

          .conexionColumnHead span {
            width: 32px;
            height: 32px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            background: rgba(204, 163, 82, 0.14);
            color: #9b7527;
            font-size: 0.78rem;
            font-weight: 900;
          }

          .conexionColumnHead h3 {
            margin: 0;
            font-size: 1rem;
            font-weight: 700;
          }

          .conexionCards {
            display: grid;
            gap: 0.8rem;
          }

          .conexionCard {
            position: relative;
            padding: 0.9rem;
            border-radius: 20px;
            background:
              linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(255, 252, 245, 0.96));
            border: 1px solid rgba(204, 163, 82, 0.2);
            box-shadow: 0 12px 28px rgba(15, 47, 70, 0.06);
          }

          .conexionCard.is-battle {
            background:
              linear-gradient(180deg, rgba(239, 247, 255, 0.98), rgba(255, 255, 255, 0.98));
            border-color: rgba(40, 107, 148, 0.2);
          }

          .conexionCard.is-victory {
            background:
              linear-gradient(180deg, rgba(240, 250, 244, 0.98), rgba(255, 255, 255, 0.98));
            border-color: rgba(69, 132, 92, 0.22);
          }

          .conexionCardTop {
            display: flex;
            justify-content: space-between;
            gap: 0.6rem;
            margin-bottom: 0.55rem;
          }

          .conexionCardTop span,
          .conexionCardTop em {
            color: #9b7527;
            font-size: 0.67rem;
            font-weight: 900;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            font-style: normal;
          }

          .conexionCard h3 {
            margin: 0;
            color: #0f2f46;
            font-size: 0.98rem;
            font-weight: 800;
            line-height: 1.25;
          }

          .conexionScriptureRef {
            margin: 0.6rem 0 0;
            color: #286b94;
            font-size: 0.86rem;
            font-weight: 900;
          }

          .conexionScriptureText,
          .conexionCardText,
          .conexionPrayerBox p,
          .conexionVictoryNote p {
            margin: 0.6rem 0 0;
            color: rgba(15, 47, 70, 0.72);
            font-size: 0.88rem;
            line-height: 1.55;
            white-space: pre-wrap;
          }

          .conexionPrayerBox,
          .conexionVictoryNote {
            margin-top: 0.8rem;
            padding: 0.75rem;
            border-radius: 16px;
            background: rgba(15, 47, 70, 0.035);
          }

          .conexionPrayerBox strong,
          .conexionVictoryNote strong {
            display: block;
            color: #0f2f46;
            font-size: 0.82rem;
            font-weight: 900;
          }

          .conexionVictoryNote small {
            display: block;
            margin-top: 0.4rem;
            color: rgba(15, 47, 70, 0.48);
          }

          .conexionCardActions {
            display: flex;
            flex-wrap: wrap;
            gap: 0.45rem;
            margin-top: 0.9rem;
          }

          .conexionCardActions button {
            min-height: auto;
            padding: 0;
            border: 0;
            border-radius: 0;
            background: transparent;
            color: rgba(15, 47, 70, 0.62);
            font-family: Questrial, sans-serif;
            font-size: 0.76rem;
            font-weight: 900;
            cursor: pointer;
            text-decoration: underline;
            text-underline-offset: 3px;
          }

          .conexionCardActions button:hover {
            color: #8a692c;
          }

          .conexionEmpty {
            margin: 0;
            color: rgba(15, 47, 70, 0.55);
            font-size: 0.92rem;
            line-height: 1.55;
          }
          
          .conexionSanctuary {
            display: grid;
            gap: 1.2rem;
          }

          .conexionWallIntro p {
            max-width: 720px;
            margin: 0.65rem 0 0;
            color: rgba(15, 47, 70, 0.62);
            font-size: 0.98rem;
            line-height: 1.65;
          }

          .conexionBoard--sanctuary {
            grid-template-columns: repeat(4, minmax(0, 1fr));
            align-items: stretch;
          }

          .conexionZone {
            min-height: 520px;
            padding: 0.9rem;
            border-radius: 28px;
            border: 1px solid rgba(204, 163, 82, 0.36);
            background:
            linear-gradient(180deg, rgba(255, 255, 255, 0.78), rgba(255, 255, 255, 0.58)),
          rgba(255, 255, 255, 0.72);
            box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.75),0 16px 38px rgba(15, 47, 70, 0.06);
          }

          .conexionZone--battle {
            background:
              radial-gradient(circle at top left, rgba(204, 163, 82, 0.18), transparent 38%),
              linear-gradient(180deg, rgba(244, 239, 225, 0.96), rgba(255, 255, 255, 0.76));
            border-color: rgba(204, 163, 82, 0.42);
          }

          .conexionZone--petition {
            background:
              radial-gradient(circle at top left, rgba(15, 47, 70, 0.13), transparent 40%),
              linear-gradient(180deg, rgba(226, 236, 240, 0.96), rgba(255, 255, 255, 0.76));
            border-color: rgba(204, 163, 82, 0.4);
          }

          .conexionZone--prayer {
            background:
              radial-gradient(circle at top left, rgba(204, 163, 82, 0.14), transparent 38%),
              linear-gradient(180deg, rgba(239, 234, 220, 0.98), rgba(255, 255, 255, 0.78));
            border-color: rgba(204, 163, 82, 0.42);
          }

          .conexionZone--victory {
            background:
              radial-gradient(circle at top left, rgba(39, 91, 76, 0.13), transparent 40%),
              linear-gradient(180deg, rgba(226, 239, 232, 0.96), rgba(255, 255, 255, 0.76));
            border-color: rgba(204, 163, 82, 0.42);
          }

          .conexionZoneHead {
            display: flex;
            gap: 0.75rem;
            align-items: flex-start;
            margin-bottom: 0.8rem;
          }

          .conexionZoneHead .conexionZoneIcon {
            width: 34px;
            height: 34px;
            flex: 0 0 34px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            background: rgba(15, 47, 70, 0.06);
            color: #0f2f46;
            font-size: 1rem;
            font-weight: 900;
          }

          .conexionZoneHead h3 {
            margin: 0;
            color: #0f2f46;
            font-size: 1rem;
            font-weight: 800;
          }

          .conexionZoneHead p {
            margin: 0.25rem 0 0;
            color: rgba(15, 47, 70, 0.56);
            font-size: 0.82rem;
            line-height: 1.45;
          }

          .conexionZoneAdd {
            width: auto;
            min-height: auto;
            margin: 0 0 0.85rem;
            padding: 0;
            border: 0;
            border-radius: 0;
            background: transparent;
            color: #8a692c;
            font-family: Questrial, sans-serif;
            font-size: 0.84rem;
            font-weight: 900;
            cursor: pointer;
            text-decoration: underline;
            text-decoration-color: rgba(204, 163, 82, 0.45);
            text-underline-offset: 4px;
          }

          .conexionZoneAdd:hover {
            color: #0f2f46;
            background: transparent;
          }

          .conexionComposerOverlay {
            position: fixed;
            inset: 0;
            z-index: 9999;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 1.25rem;
            background: rgba(15, 47, 70, 0.34);
            backdrop-filter: blur(8px);
          }

          .conexionComposerPanel {
            width: min(860px, 100%);
            max-height: calc(100vh - 2.5rem);
            overflow: auto;
            padding: 1.25rem;
            border-radius: 30px;
            background:
             linear-gradient(180deg, rgba(255,255,255,0.98), rgba(255,252,245,0.98));
             border: 1px solid rgba(204, 163, 82, 0.24);
             box-shadow: 0 30px 90px rgba(15, 47, 70, 0.22);
          }

          .conexionComposerHead {
            display: flex;
            justify-content: space-between;
            gap: 1rem;
            align-items: flex-start;
            margin-bottom: 1rem;
          }

          .conexionComposerHead span {
            display: inline-flex;
            margin-bottom: 0.5rem;
            padding: 0.35rem 0.7rem;
            border-radius: 999px;
            background: rgba(204, 163, 82, 0.12);
            color: #9b7527;
            font-size: 0.7rem;
            font-weight: 900;
            letter-spacing: 0.12em;
            text-transform: uppercase;
          }

          .conexionComposerHead h2 {
            margin: 0;
            color: #0f2f46;
            font-size: 1.65rem;
            font-weight: 500;
            letter-spacing: -0.035em;
          }

          .conexionComposerHead button {
            width: 42px;
            height: 42px;
            border-radius: 50%;
            border: 1px solid rgba(15, 47, 70, 0.1);
            background: #fff;
            color: #0f2f46;
            font-size: 1.5rem;
            cursor: pointer;
          }

          .conexionComposerGrid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0.85rem;
          }

          .conexionComposerGrid label {
            display: grid;
            gap: 0.38rem;
            color: rgba(15, 47, 70, 0.68);
            font-size: 0.78rem;
            font-weight: 900;
          }

          .conexionComposerGrid input,
          .conexionComposerGrid select,
          .conexionComposerGrid textarea {
            width: 100%;
            box-sizing: border-box;
            border-radius: 16px;
            border: 1px solid rgba(15, 47, 70, 0.12);
            background: #fff;
            color: #0f2f46;
            font-family: Questrial, sans-serif;
            font-size: 0.96rem;
            line-height: 1.55;
            outline: none;
          }

          .conexionComposerGrid input,
          .conexionComposerGrid select {
            min-height: 44px;
            padding: 0 0.85rem;
          }

          .conexionComposerGrid textarea {
            min-height: 96px;
            resize: vertical;
            padding: 0.8rem 0.85rem;
          }

          .conexionComposerFull {
            grid-column: 1 / -1;
          }

          .conexionPrayerAssistHeader {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1rem;
          }

          .conexionPrayerAssistHeader span {
            color: rgba(15, 47, 70, 0.72);
            font-size: 0.88rem;
            font-weight: 800;
          }

          .conexionPrayerAssistButton {
            min-height: 42px;
            padding: 0 1.15rem;
            border-radius: 999px;
            border: 1px solid rgba(204, 163, 82, 0.46);
            background: linear-gradient(135deg, #0f2f46 0%, #173f5b 52%, #0f2f46 100%);
            color: #f4d27a;
            font-family: Questrial, sans-serif;
            font-size: 0.88rem;
            font-weight: 900;
            letter-spacing: -0.01em;
            cursor: pointer;
            box-shadow: 0 14px 30px rgba(15, 47, 70, 0.16);
            transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
          }

          .conexionPrayerAssistButton:hover {
            transform: translateY(-1px);
            border-color: rgba(204, 163, 82, 0.68);
            box-shadow: 0 18px 38px rgba(15, 47, 70, 0.22);
          }

          .conexionPrayerAssistButton:disabled {
            opacity: 0.68;
            cursor: not-allowed;
            transform: none;
          }

          .conexionWallIntro--compact h2 {
            margin-bottom: 0;
          }

          .conexionGuideAction {
            margin-top: 1.15rem;
          }

          .conexionGuideAction button {
            min-height: 42px;
            padding: 0 1.1rem;
            border-radius: 999px;
            border: 1px solid rgba(204, 163, 82, 0.34);
            background: linear-gradient(180deg, rgba(204, 163, 82, 0.12), rgba(255, 255, 255, 0.88));
            color: #7b6430;
            font-family: Questrial, sans-serif;
            font-size: 0.88rem;
            font-weight: 900;
            cursor: pointer;
            box-shadow: 0 12px 28px rgba(15, 47, 70, 0.05);
          }

          .conexionGuideAction button:hover {
            transform: translateY(-1px);
            border-color: rgba(204, 163, 82, 0.5);
            box-shadow: 0 16px 34px rgba(15, 47, 70, 0.08);
          }

          .conexionGuideOverlay {
            position: fixed;
            inset: 0;
            z-index: 9999;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 1.25rem;
            background: rgba(15, 47, 70, 0.38);
            backdrop-filter: blur(8px);
          }

          .conexionGuideModal {
            width: min(820px, 100%);
            padding: 1.1rem;
            border-radius: 30px;
            border: 1px solid rgba(204, 163, 82, 0.28);
            background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(255, 252, 245, 0.98));
            box-shadow: 0 30px 90px rgba(15, 47, 70, 0.24);
          }

          .conexionGuideModalHead {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 1rem;
            margin-bottom: 0.9rem;
          }

          .conexionGuideModalHead span {
            display: inline-flex;
            margin-bottom: 0.45rem;
            padding: 0.35rem 0.7rem;
            border-radius: 999px;
            background: rgba(204, 163, 82, 0.13);
            color: #9b7527;
            font-size: 0.7rem;
            font-weight: 900;
            letter-spacing: 0.12em;
            text-transform: uppercase;
          }

          .conexionGuideModalHead h2 {
            margin: 0;
            color: #0f2f46;
            font-size: 1.35rem;
            font-weight: 700;
            letter-spacing: -0.035em;
          }

          .conexionGuideModalHead button {
            width: 42px;
            height: 42px;
            border-radius: 50%;
            border: 1px solid rgba(15, 47, 70, 0.1);
            background: #fff;
            color: #0f2f46;
            font-size: 1.5rem;
            cursor: pointer;
          }

          .conexionGuideModalVideo {
            width: 100%;
            display: block;
            border-radius: 22px;
            background: #0f2f46;
            box-shadow: 0 16px 34px rgba(15, 47, 70, 0.14);
          }

          .conexionScriptureHelp {
            margin: 0 0 0.45rem;
            color: rgba(15, 47, 70, 0.58);
            font-size: 0.84rem;
            line-height: 1.5;
            font-weight: 500;
          }

          .conexionWallIntro {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1rem;
            margin-bottom: 1.15rem;
          }

          .conexionSaveWallButton {
            min-height: 38px;
            padding: 0 1rem;
            border-radius: 999px;
            border: 1px solid rgba(204, 163, 82, 0.42);
            background: rgba(255, 248, 232, 0.12);
            color: #e3c570;
            font-family: Questrial, sans-serif;
            font-size: 0.82rem;
            font-weight: 900;
            cursor: pointer;
            box-shadow: 0 12px 28px rgba(6, 18, 28, 0.12);
          }

          .conexionSaveWallButton:hover {
            background: rgba(204, 163, 82, 0.18);
          }

          .conexionSaveWallButton:disabled {
            opacity: 0.65;
            cursor: not-allowed;
          }

          @media (max-width: 1080px) {
            .conexionLayout {
              grid-template-columns: 1fr;
            }

            .conexionCommand {
              position: relative;
              top: auto;
            }
            
            .conexionBoard--sanctuary {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }
          }

          @media (max-width: 860px) {
            .conexionBoard {
              grid-template-columns: 1fr;
            }

            .conexionColumn {
              min-height: auto;
            }

            .conexionHeader h1 {
              font-size: 2.65rem;
            }
            
            .conexionBoard--sanctuary {
              grid-template-columns: 1fr;
            }

            .conexionZone {
              min-height: auto;
            }

            .conexionFocusNote {
              max-width: 100%;
              width: 100%;
              box-sizing: border-box;
            }

            .conexionHeroCopy {
              grid-template-columns: 1fr;
              gap: 0.75rem;
              max-width: 100%;
             }

            .conexionHeroCopy p + p {
              padding-left: 0;
              border-left: 0;
            }

            .conexionHeroCopy p {
              font-size: 0.98rem;
              line-height: 1.7;
            }

            .conexionWall {
              padding: 1rem 1rem 1.15rem;
            }

            .conexionGuideVideo {
              grid-template-columns: 1fr;
              max-width: 100%;
            }

            .conexionVideoPlayer {
              max-height: none;
            }

            .conexionHeroText {
              max-width: 100%;
              font-size: 0.98rem;
            }

            .conexionWallIntro {
              align-items: flex-start;
              flex-direction: column;
            }

            .conexionSaveWallButton {
              width: 100%;
            }
          }

          @media (max-width: 560px) {
            .conexionPage {
              padding: 1.15rem 0.75rem 3rem;
            }

            .conexionWall,
            .conexionCommand {
              border-radius: 26px;
            }

            .conexionHeader p {
              font-size: 1.03rem;
              line-height: 1.72;
            }

            .conexionCommand input,
            .conexionCommand select {
              min-height: 50px;
              font-size: 1rem;
            }

            .conexionCommand textarea {
              font-size: 1rem;
            }

            .conexionCommandActions button {
              width: 100%;
            }

            .conexionComposerGrid {
              grid-template-columns: 1fr;
            }

            .conexionComposerPanel {
              border-radius: 24px;
              padding: 1rem;
            }

            .conexionPrayerAssistHeader {
              align-items: flex-start;
              flex-direction: column;
            }

            .conexionPrayerAssistHeader button {
              width: 100%;
            }
          }
        `}
      </style>
    </main>
  );
}